import React, { useEffect, useMemo, useState } from 'react';
import { useLoaderContext } from '../../../contexts/LoaderContext';
import { useDateFormat } from '../../../hooks/useDateFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MarsApiService as api } from '../../../api/mars-api-service';
import { MarsApiConfig as apiConfig } from '../../../api/mars-api-config';
import { IGridConfig } from '../../../types/grid';
import { Workbook } from 'exceljs';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import { exportDataGrid } from 'devextreme/excel_exporter';
import saveAs from 'file-saver';
import jsPDF from 'jspdf';
import { useResize } from '../../../hooks/useResize';
import Button from 'devextreme-react/button';
import icons from '../../common/icons/icons';
import Popover from 'devextreme-react/popover';
import Grid from '../../common/grid/Grid';
import { getCurrentBuId } from '../../../utils/jwt-decode';
import { SanctionsCheckList } from '../../../types/sanctions-check';

export default React.memo(function RecentSanctionsCheck() {
  const { setIsLoading } = useLoaderContext();
  const [recentSanctionsChecks, setRecentSanctionsCheck] =
    useState<SanctionsCheckList[]>();
  const { formatDate } = useDateFormat();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [gridInstance, setGridInstance] = useState<any>();
  const [showExportPopover, setShowExportPopover] = useState(false);
  const exportPopupAttributes = useMemo(() => {
    return {
      id: 'elementId',
      class: 'm-l-dashboard__chart-export',
      'data-testid': 'rolePopover',
    };
  }, []);

  const onRowClick = (e: any) => {
    if (e.data) {
      navigate('/sanctions-check', {
        state: {
          imoNumber: e.data.imoNumber,
          sanctionsCheckId: e.data.sanctionCheckId,
          vetRequestId: e.data.vetRequestId,
        },
      });
    }
  };

  useEffect(() => {
    getSanctionsList();
  }, []);

  const getSanctionsList = async () => {
    const buId = getCurrentBuId();
    await api
      .get(
        {
          url: apiConfig.sanctionsCheckHistory,
          params: { buid: buId, isRequestedBy: true },
        },
        setIsLoading
      )
      .then((res) => {
        setRecentSanctionsCheck(res);
      });
  };

  const onGridInitialized = (e: any) => {
    setGridInstance(e.component);
  };

  const gridConfig: IGridConfig = {
    dataSource: recentSanctionsChecks,
    defaultColumns: [
      {
        dataField: 'sanctionCheckId',
        dataType: 'string',
        minWidth: 50,
        caption: t('labels.sanctionsCheckId'),
      },
      {
        dataField: 'vesselName',
        dataType: 'string',
        minWidth: 50,
        caption: t('labels.vesselName'),
      },
      {
        dataField: 'requestedDateTime',
        dataType: 'string',
        minWidth: 50,
        caption: t('labels.date'),
        cellTemplate: 'actionTemplate',
      },
    ],
    rowClick: onRowClick,
    hideFilterRow: true,
    hidePagination: true,
    initialized: onGridInitialized,
  };

  const fileFormats = [
    {
      id: 1,
      name: 'Excel File',
      value: 'xlsx',
    },
    {
      id: 2,
      name: 'PDF File',
      value: 'pdf',
    },
  ];

  const exportRolesGrid = (format: any) => {
    if (format === 'xlsx') {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');

      exportDataGrid({
        component: gridInstance,
        worksheet: worksheet,
      }).then(function () {
        workbook.xlsx.writeBuffer().then(function (buffer) {
          saveAs(
            new Blob([buffer], { type: 'application/octet-stream' }),
            `RecentSanctionsChecks.${format}`
          );
        });
      });
    } else if (format === 'pdf') {
      const doc = new jsPDF();
      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: gridInstance,
      }).then(() => {
        doc.save('RecentSanctionsChecks.pdf');
      });
    }
  };

  const { isMobileResolution } = useResize();
  const [position, setPosition] = useState<any>('bottom');
  useEffect(() => {
    if (isMobileResolution) {
      setPosition('left');
    } else {
      setPosition('bottom');
    }
  }, [isMobileResolution]);

  function dateCellTemplate(data: any) {
    const formatedDate = formatDate(data.value);
    return <div className="m-l-badge-container">{formatedDate}</div>;
  }

  return (
    <div className="m-c-grid m-c-block-grid-list">
      <div className="m-c-form-group">
        <div className="row">
          <div className="col-12 d-flex ps-4 pe-4">
            <div className="ml-dashboard-export">
              <Button
                id="linkRecentSanctionsChecks"
                className="app-c-btn app-c-btn--secondary m-c-icon-inside-secondary-button"
                aria-label="Export-btn"
                onClick={() => setShowExportPopover(true)}
              >
                <div className="m-c-icon-inside-btn" data-testid="export-all">
                  <icons.download />
                </div>
              </Button>
              <Popover
                target="#linkRecentSanctionsChecks"
                position={position}
                width={132}
                visible={showExportPopover}
                wrapperAttr={exportPopupAttributes}
                hideOnOutsideClick={true}
                onHiding={() => setShowExportPopover(false)}
              >
                <ul className="ml-expport-list">
                  {/* <li>Print</li> */}
                  {fileFormats.map((role: any, index: any) => {
                    return (
                      <li
                        key={index}
                        onClick={() => exportRolesGrid(role.value)}
                      >
                        {role.name}
                      </li>
                    );
                  })}
                </ul>
              </Popover>
            </div>
          </div>
        </div>
      </div>
      <Grid gridConfig={gridConfig} actionTemplate={dateCellTemplate} />
    </div>
  );
});
